/**
 * OJAR Gift Box Cart Logic v6.1
 * Features:
 * - Visual grouping with bundle wrapper
 * - Single "Remove Gift Box" button (atomic bundle behavior)
 * - HIDE individual delete icons on gift box items
 * - Hide quantity controls on gift box items (atomic bundle)
 * - Branded SweetAlert UI for "Remove Gift Box" confirmation
 * - Loading state during removal
 * - MULTI-TRIGGER grouping with MutationObserver (v6.1)
 * 
 * v6.1 Changes:
 * - MutationObserver watches cart drawer DOM
 * - Cart icon click triggers grouping with retries
 * - cartUpdate event triggers grouping
 * - Initial setTimeout retries (500ms, 1s, 2s)
 * - Handles late-mounted cart drawer items
 * 
 * v6 Changes:
 * - Individual delete icons on gift box items HIDDEN
 * - Only "Remove Gift Box" button can remove the bundle
 * - Eliminates popup confusion (single clear action)
 * - Quantity controls also hidden (bundle is atomic)
 */

(function() {
    'use strict';
    
    console.log('[OJ Gift Box Cart] Initialized v6.1');
    
    let isProcessing = false;
    let cartCache = null;
    
    /**
     * Load SweetAlert2 with custom CSS
     */
    function loadSweetAlert() {
        return new Promise((resolve) => {
            if (window.Swal) {
                injectCustomStyles();
                resolve();
                return;
            }
            
            const script = document.createElement('script');
            script.src = 'https://cdn.jsdelivr.net/npm/sweetalert2@11/dist/sweetalert2.all.min.js';
            script.onload = () => {
                console.log('[OJ Gift Box Cart] SweetAlert2 loaded');
                injectCustomStyles();
                resolve();
            };
            script.onerror = () => {
                console.warn('[OJ Gift Box Cart] SweetAlert2 failed to load');
                resolve();
            };
            document.head.appendChild(script);
        });
    }
    
    /**
     * Inject custom OJAR branded styles for SweetAlert + hide gift box delete buttons
     */
    function injectCustomStyles() {
        if (document.getElementById('oj-gift-box-swal-styles')) return;
        
        const style = document.createElement('style');
        style.id = 'oj-gift-box-swal-styles';
        style.textContent = `
            /* ===== HIDE INDIVIDUAL DELETE BUTTONS ON GIFT BOX ITEMS (v6) ===== */
            /* Bundle is atomic — only "Remove Gift Box" button can remove items */
            .oj-gift-box-bundle__items cart-remove-button,
            .oj-gift-box-bundle__items .cart-remove-button,
            .oj-gift-box-bundle__items .cart-item__remove,
            tr.oj-gift-box-bundle__items cart-remove-button,
            tr.oj-gift-box-bundle__items .cart-remove-button,
            tr.oj-gift-box-bundle__items .cart-item__remove {
                display: none !important;
                visibility: hidden !important;
                pointer-events: none !important;
            }
            
            /* Hide quantity controls on gift box items (atomic bundle) */
            .oj-gift-box-bundle__items quantity-input,
            tr.oj-gift-box-bundle__items quantity-input,
            .oj-gift-box-bundle__items .quantity,
            tr.oj-gift-box-bundle__items .quantity {
                pointer-events: none !important;
                opacity: 0.6 !important;
            }
            
            /* Hide quantity +/- buttons specifically */
            .oj-gift-box-bundle__items .quantity__button,
            tr.oj-gift-box-bundle__items .quantity__button {
                visibility: hidden !important;
                pointer-events: none !important;
            }
            
            /* ===== SWEETALERT BRANDED STYLES ===== */
            .oj-gift-box-swal {
                font-family: inherit !important;
                border-radius: 4px !important;
                padding: 32px 28px !important;
                max-width: 480px !important;
                width: 90% !important;
                box-shadow: 0 20px 60px rgba(0,0,0,0.25) !important;
            }
            
            .oj-gift-box-swal .swal2-icon {
                width: 70px !important;
                height: 70px !important;
                margin: 0 auto 20px !important;
                border-color: #c89e62 !important;
            }
            
            .oj-gift-box-swal .swal2-icon-content,
            .oj-gift-box-swal .swal2-icon.swal2-warning {
                color: #c89e62 !important;
                font-size: 50px !important;
                border-color: #c89e62 !important;
            }
            
            .oj-gift-box-swal .swal2-title {
                font-size: 22px !important;
                font-weight: 500 !important;
                color: #1a1a1a !important;
                letter-spacing: 0.5px !important;
                margin-bottom: 12px !important;
                padding: 0 !important;
            }
            
            .oj-gift-box-swal .swal2-html-container {
                font-size: 15px !important;
                color: #4a4a4a !important;
                line-height: 1.6 !important;
                margin: 12px 0 24px !important;
            }
            
            .oj-gift-box-swal .swal2-html-container strong {
                color: #1a1a1a !important;
                font-weight: 600 !important;
            }
            
            .oj-gift-box-swal .swal2-actions {
                gap: 12px !important;
                margin-top: 24px !important;
            }
            
            .oj-gift-box-swal .swal2-confirm,
            .oj-gift-box-swal .swal2-cancel {
                padding: 12px 28px !important;
                font-size: 13px !important;
                font-weight: 500 !important;
                letter-spacing: 1.5px !important;
                text-transform: uppercase !important;
                border-radius: 0 !important;
                border: 1px solid #1a1a1a !important;
                box-shadow: none !important;
                transition: all 0.2s ease !important;
                min-width: 140px !important;
            }
            
            .oj-gift-box-swal .swal2-confirm {
                background: #1a1a1a !important;
                color: white !important;
            }
            
            .oj-gift-box-swal .swal2-confirm:hover {
                background: #333 !important;
                border-color: #333 !important;
            }
            
            .oj-gift-box-swal .swal2-cancel {
                background: white !important;
                color: #1a1a1a !important;
            }
            
            .oj-gift-box-swal .swal2-cancel:hover {
                background: #f5f5f5 !important;
            }
            
            .oj-gift-box-swal-success {
                font-family: inherit !important;
                border-radius: 4px !important;
                padding: 28px !important;
            }
            
            .oj-gift-box-swal-success .swal2-icon {
                border-color: #c89e62 !important;
                color: #c89e62 !important;
            }
            
            .oj-gift-box-swal-success .swal2-icon .swal2-success-line-tip,
            .oj-gift-box-swal-success .swal2-icon .swal2-success-line-long {
                background-color: #c89e62 !important;
            }
            
            .oj-gift-box-swal-success .swal2-icon .swal2-success-ring {
                border-color: rgba(200, 158, 98, 0.3) !important;
            }
            
            .oj-gift-box-swal-success .swal2-title {
                font-size: 18px !important;
                font-weight: 500 !important;
                color: #1a1a1a !important;
            }
            
            .oj-gift-box-swal-success .swal2-html-container {
                font-size: 14px !important;
                color: #666 !important;
            }
            
            .oj-gift-box-swal-loading {
                font-family: inherit !important;
                border-radius: 4px !important;
                padding: 32px !important;
            }
            
            .oj-gift-box-swal-loading .swal2-title {
                font-size: 16px !important;
                color: #1a1a1a !important;
                margin-top: 16px !important;
            }
            
            .oj-gift-box-swal-loading .swal2-loader {
                border-color: #c89e62 transparent #c89e62 transparent !important;
                width: 50px !important;
                height: 50px !important;
            }
        `;
        document.head.appendChild(style);
    }
    
    /**
     * Get fresh cart data
     */
    async function getCart() {
        const response = await fetch('/cart.js', { 
            cache: 'no-store',
            headers: { 'Cache-Control': 'no-cache' }
        });
        return response.json();
    }
    
    /**
     * Build map of cart items → gift box info
     */
    async function rebuildCartCache() {
        const cart = await getCart();
        cartCache = {};
        cartCache._groups = {};
        
        cart.items.forEach((item, index) => {
            if (item.properties && item.properties._gift_box === 'true') {
                const groupId = item.properties._gift_box_group_id;
                
                cartCache[item.key] = {
                    groupId: groupId,
                    boxName: item.properties._gift_box_name || 'Gift Box',
                    isGiftBox: true
                };
                
                if (!cartCache._groups[groupId]) {
                    cartCache._groups[groupId] = {
                        keys: [],
                        lines: [],
                        boxName: item.properties._gift_box_name || 'Gift Box'
                    };
                }
                cartCache._groups[groupId].keys.push(item.key);
                cartCache._groups[groupId].lines.push(index + 1);
            }
        });
        
        const itemCount = Object.keys(cartCache).filter(k => k !== '_groups').length;
        console.log('[OJ Gift Box Cart] Cache built:', itemCount, 'gift box items');
    }
    
    /**
     * Remove items by line numbers (descending order to avoid shift issue)
     */
    async function removeItemsByLines(lines) {
        console.log('[OJ Gift Box Cart] Removing lines:', lines);
        
        const sortedLines = [...lines].sort((a, b) => b - a);
        const results = [];
        
        for (const line of sortedLines) {
            const response = await fetch('/cart/change.js', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json'
                },
                body: JSON.stringify({
                    line: line,
                    quantity: 0
                })
            });
            
            const result = await response.json();
            console.log('[OJ Gift Box Cart] Removed line', line, 'Status:', response.status);
            
            if (!response.ok) {
                throw new Error(result.description || 'Removal failed for line ' + line);
            }
            
            results.push(result);
        }
        
        return results[results.length - 1];
    }
    
    /**
     * Show confirmation dialog
     */
    async function showConfirmation(groupInfo, itemCount) {
        if (!window.Swal) {
            return confirm(`This will remove "${groupInfo.boxName}" and all ${itemCount} items in this gift box. Continue?`);
        }
        
        const result = await window.Swal.fire({
            title: 'Remove Gift Box?',
            html: `
                <div style="margin: 8px 0;">
                    <p style="margin-bottom: 14px; font-size: 16px;">
                        You're about to remove <strong>"${groupInfo.boxName}"</strong>
                    </p>
                    <p style="font-size: 14px; color: #666;">
                        This will remove all <strong>${itemCount} items</strong> in this gift box together.
                    </p>
                </div>
            `,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Remove Gift Box',
            cancelButtonText: 'Keep It',
            reverseButtons: true,
            customClass: {
                popup: 'oj-gift-box-swal'
            },
            buttonsStyling: false,
            allowOutsideClick: false
        });
        
        return result.isConfirmed;
    }
    
    /**
     * Show loading indicator
     */
    function showLoading() {
        if (!window.Swal) return;
        
        window.Swal.fire({
            title: 'Removing gift box...',
            html: '<p style="font-size: 13px; color: #999; margin-top: 8px;">Please wait a moment</p>',
            allowOutsideClick: false,
            allowEscapeKey: false,
            showConfirmButton: false,
            customClass: {
                popup: 'oj-gift-box-swal-loading'
            },
            didOpen: () => {
                window.Swal.showLoading();
            }
        });
    }
    
    /**
     * Show success message
     */
    function showSuccess(boxName) {
        if (!window.Swal) return;
        
        window.Swal.fire({
            title: 'Removed Successfully',
            html: `<p>${boxName} has been removed from your cart.</p>`,
            icon: 'success',
            timer: 1200,
            showConfirmButton: false,
            customClass: {
                popup: 'oj-gift-box-swal-success'
            }
        });
    }
    
    /**
     * Show error message
     */
    function showError(message) {
        if (!window.Swal) {
            alert('Error: ' + message);
            return;
        }
        
        window.Swal.fire({
            title: 'Something went wrong',
            text: message,
            icon: 'error',
            confirmButtonText: 'OK',
            customClass: {
                popup: 'oj-gift-box-swal'
            },
            buttonsStyling: false
        });
    }
    
    /**
     * Visually group gift box items in cart
     */
    function applyVisualGrouping() {
        if (!cartCache || !cartCache._groups) return;
        
        Object.keys(cartCache._groups).forEach(groupId => {
            const group = cartCache._groups[groupId];
            
            // Skip if already wrapped
            const existingWrapper = document.querySelector(`[data-gift-box-group="${groupId}"]`);
            if (existingWrapper) return;
            
            // Find all cart item DOM elements for this group
            const groupItemElements = [];
            group.keys.forEach(key => {
                let item = null;
                
                // Method 1: data-key attribute with CSS.escape for colon
                try {
                    item = document.querySelector(`[data-key="${CSS.escape(key)}"]`);
                } catch (e) {}
                
                // Method 2: Find by iterating all cart items
                if (!item) {
                    const allItems = document.querySelectorAll('tr.cart-item, .cart-item, [data-key]');
                    allItems.forEach(el => {
                        if (el.getAttribute('data-key') === key) {
                            item = el;
                        }
                    });
                }
                
                // Method 3: Find by class containing key
                if (!item) {
                    try {
                        item = document.querySelector(`tr.cart-item.${CSS.escape(key)}`);
                    } catch (e) {}
                }
                
                if (item) {
                    console.log('[OJ Gift Box Cart] Found item DOM for key:', key);
                    groupItemElements.push(item);
                } else {
                    console.warn('[OJ Gift Box Cart] Could not find DOM for key:', key);
                }
            });
            
            if (groupItemElements.length === 0) {
                console.warn('[OJ Gift Box Cart] No DOM items found for group:', groupId);
                return;
            }
            
            console.log('[OJ Gift Box Cart] Visual grouping:', group.boxName, '(' + groupItemElements.length + ' items)');
            
            const firstItem = groupItemElements[0];
            const isTableRow = firstItem.tagName === 'TR';
            
            if (isTableRow) {
                wrapTableRows(groupId, group, groupItemElements);
            } else {
                wrapBlockElements(groupId, group, groupItemElements);
            }
            
            // v6: Hide delete icons and quantity controls on gift box items
            // This makes the bundle atomic — only "Remove Gift Box" button works
            hideIndividualControls(groupItemElements);
        });
    }
    
    /**
     * v6: Hide individual delete icons and quantity controls on gift box items
     * Bundle is atomic — only "Remove Gift Box" button can modify it
     */
    function hideIndividualControls(items) {
        items.forEach(row => {
            // Hide delete buttons (cart-remove-button, .cart-remove-button, etc.)
            const deleteElements = row.querySelectorAll(
                'cart-remove-button, ' +
                '.cart-remove-button, ' +
                '.cart-item__remove, ' +
                '.cart-item__remove a, ' +
                '.cart-item__remove button, ' +
                'a[href*="quantity=0"]'
            );
            deleteElements.forEach(el => {
                el.style.display = 'none';
                el.style.visibility = 'hidden';
                el.style.pointerEvents = 'none';
                el.setAttribute('aria-hidden', 'true');
                el.setAttribute('tabindex', '-1');
            });
            
            // Also hide via inline style as defense (CSS class also handles this)
            console.log('[OJ Gift Box Cart] Hidden controls on gift box item');
        });
    }
    
    /**
     * Wrap table rows (cart page typically uses table)
     */
    function wrapTableRows(groupId, group, items) {
        const firstRow = items[0];
        
        // Create header row
        const headerRow = document.createElement('tr');
        headerRow.className = 'oj-gift-box-bundle-header-row';
        headerRow.setAttribute('data-gift-box-group', groupId);
        headerRow.innerHTML = `
            <td colspan="100" style="padding: 0;">
                <div class="oj-gift-box-bundle__header">
                    <span class="oj-gift-box-bundle__header-icon">🎁</span>
                    <div class="oj-gift-box-bundle__header-content">
                        <h3 class="oj-gift-box-bundle__header-title">${group.boxName}</h3>
                        <p class="oj-gift-box-bundle__header-subtitle">${group.keys.length} items • 20% OFF applied at checkout</p>
                    </div>
                </div>
            </td>
        `;
        
        // Create footer row
        const footerRow = document.createElement('tr');
        footerRow.className = 'oj-gift-box-bundle-footer-row';
        footerRow.innerHTML = `
            <td colspan="100" style="padding: 0;">
                <div class="oj-gift-box-bundle__footer">
                    <button type="button" class="oj-gift-box-bundle__remove-btn" data-gift-box-remove="${groupId}">
                        <span class="oj-gift-box-bundle__remove-btn-icon">🗑</span>Remove Gift Box
                    </button>
                </div>
            </td>
        `;
        
        // Insert header before first item
        firstRow.parentNode.insertBefore(headerRow, firstRow);
        
        // Insert footer after last item
        const lastRow = items[items.length - 1];
        if (lastRow.nextSibling) {
            lastRow.parentNode.insertBefore(footerRow, lastRow.nextSibling);
        } else {
            lastRow.parentNode.appendChild(footerRow);
        }
        
        // Add bundle class to item rows for styling
        items.forEach(row => {
            row.classList.add('oj-gift-box-bundle__items');
        });
    }
    
    /**
     * Wrap block elements (cart drawer typically uses li or div)
     */
    function wrapBlockElements(groupId, group, items) {
        const firstItem = items[0];
        const parent = firstItem.parentNode;
        
        const wrapper = document.createElement('div');
        wrapper.className = 'oj-gift-box-bundle';
        wrapper.setAttribute('data-gift-box-group', groupId);
        wrapper.innerHTML = `
            <div class="oj-gift-box-bundle__header">
                <span class="oj-gift-box-bundle__header-icon">🎁</span>
                <div class="oj-gift-box-bundle__header-content">
                    <h3 class="oj-gift-box-bundle__header-title">${group.boxName}</h3>
                    <p class="oj-gift-box-bundle__header-subtitle">${group.keys.length} items • 20% OFF applied at checkout</p>
                </div>
            </div>
            <div class="oj-gift-box-bundle__items"></div>
            <div class="oj-gift-box-bundle__footer">
                <button type="button" class="oj-gift-box-bundle__remove-btn" data-gift-box-remove="${groupId}">
                    <span class="oj-gift-box-bundle__remove-btn-icon">🗑</span>Remove Gift Box
                </button>
            </div>
        `;
        
        // Insert wrapper before first item
        parent.insertBefore(wrapper, firstItem);
        
        // Move all items into wrapper
        const itemsContainer = wrapper.querySelector('.oj-gift-box-bundle__items');
        items.forEach(item => {
            itemsContainer.appendChild(item);
        });
    }
    
    /**
     * Handle bundle remove button click — the ONLY way to remove gift box in v6
     */
    function attachBundleRemoveHandler() {
        document.addEventListener('click', async function(e) {
            const btn = e.target.closest('[data-gift-box-remove]');
            if (!btn) return;
            
            e.preventDefault();
            e.stopPropagation();
            
            const groupId = btn.getAttribute('data-gift-box-remove');
            const group = cartCache?._groups?.[groupId];
            
            if (!group) {
                console.warn('[OJ Gift Box Cart] Group not found in cache:', groupId);
                return;
            }
            
            if (isProcessing) return;
            isProcessing = true;
            
            try {
                const confirmed = await showConfirmation(
                    { boxName: group.boxName },
                    group.keys.length
                );
                
                if (!confirmed) {
                    console.log('[OJ Gift Box Cart] User cancelled bundle removal');
                    isProcessing = false;
                    return;
                }
                
                showLoading();
                await removeItemsByLines(group.lines);
                showSuccess(group.boxName);
                
                setTimeout(() => {
                    window.location.reload();
                }, 1200);
            } catch (err) {
                console.error('[OJ Gift Box Cart] Error:', err);
                showError('Failed to remove gift box. Please refresh and try again.');
                isProcessing = false;
            }
        });
    }
    
    /**
     * v6: Block any individual delete clicks on gift box items (defensive)
     * Even if CSS fails to hide buttons, this prevents the action
     */
    function attachDefensiveBlocker() {
        document.addEventListener('click', function(e) {
            const removeBtn = e.target.closest(
                'cart-remove-button, ' +
                '.cart-remove-button, ' +
                '.cart-item__remove, ' +
                '.cart-item__remove a, ' +
                '.cart-item__remove button, ' +
                'a[href*="quantity=0"]'
            );
            
            if (!removeBtn) return;
            
            // Allow our bundle remove button to work
            if (removeBtn.hasAttribute('data-gift-box-remove') || 
                removeBtn.classList.contains('oj-gift-box-bundle__remove-btn')) {
                return;
            }
            
            // Check if click happened inside a gift box item row
            const giftBoxRow = removeBtn.closest('.oj-gift-box-bundle__items');
            
            if (giftBoxRow) {
                // Block individual delete on gift box items
                e.preventDefault();
                e.stopImmediatePropagation();
                e.stopPropagation();
                console.log('[OJ Gift Box Cart] Blocked individual delete on gift box item');
                return false;
            }
        }, true); // Capture phase to intercept before native handlers
    }
    
    /**
     * v6.1: Re-apply grouping with cache rebuild — handles cart drawer late mount
     */
    async function refreshGrouping() {
        try {
            await rebuildCartCache();

            // Disconnect the drawer observer while we mutate the DOM ourselves,
            // so our own grouping changes don't re-trigger the observer (loop).
            var obs = window._ojGiftBoxObserver;
            var drawerEl = document.querySelector('cart-drawer');
            if (obs) obs.disconnect();

            applyVisualGrouping();

            // Reconnect after our DOM writes settle.
            if (obs && drawerEl) {
                setTimeout(function () {
                    obs.observe(drawerEl, { childList: true, subtree: true });
                }, 50);
            }
        } catch (e) {
            console.warn('[OJ Gift Box Cart] Refresh grouping failed:', e);
        }
    }

    /**
     * v6.1: Setup MutationObserver on cart drawer to detect when items appear
     * This handles the case where cart drawer renders items AFTER init() runs
     */
    function setupCartDrawerObserver() {
        const drawer = document.querySelector('cart-drawer');
        if (!drawer) {
            // Drawer not yet in DOM — retry after 500ms
            setTimeout(setupCartDrawerObserver, 500);
            return;
        }

        let debounceTimer = null;
        const observer = new MutationObserver(function () {
            // Only act if the cart actually contains gift box items. Without this,
            // a cart with no gift box makes (hasItems && !hasWrapper) always true,
            // so every DOM change triggers a /cart.js fetch — an endless loop with
            // the currency/samples scripts that also mutate the drawer.
            var giftBoxCount = cartCache
                ? Object.keys(cartCache).filter(function (k) { return k !== '_groups'; }).length
                : 0;
            if (giftBoxCount === 0) return;

            // Check if drawer has product items but no wrapper yet
            const hasWrapper = drawer.querySelector('[data-gift-box-group]');
            if (hasWrapper) return; // already grouped — nothing to do

            // Debounce to avoid running on every mutation
            clearTimeout(debounceTimer);
            debounceTimer = setTimeout(function () {
                refreshGrouping();
            }, 200);
        });

        window._ojGiftBoxObserver = observer;
        observer.observe(drawer, {
            childList: true,
            subtree: true
        });

        console.log('[OJ Gift Box Cart] Drawer observer active');
    }

    /**
     * v6.1: Trigger grouping when cart icon clicked (drawer opening)
     */
    function setupCartIconListener() {
        document.addEventListener('click', function (e) {
            const trigger = e.target.closest(
                '[data-cart-drawer-trigger], ' +
                '[href*="/cart"], ' +
                '.cart-icon-bubble, ' +
                '#cart-icon-bubble, ' +
                '.header__icon--cart'
            );
            if (trigger) {
                // Drawer opening — apply grouping after a delay
                setTimeout(refreshGrouping, 200);
                setTimeout(refreshGrouping, 500);  // Backup retry
                setTimeout(refreshGrouping, 1000); // Final retry
            }
        }, true);
    }

    /**
     * Initialize
     */
    async function init() {
        await loadSweetAlert();
        await rebuildCartCache();
        
        // Attach bundle remove button handler
        attachBundleRemoveHandler();
        
        // v6: Defensive blocker for individual delete clicks
        attachDefensiveBlocker();
        
        // Apply visual grouping (also hides individual controls)
        applyVisualGrouping();
        
        // Refresh cache when cart updates
        if (window.subscribe && window.PUB_SUB_EVENTS) {
            if (window.PUB_SUB_EVENTS.cartUpdate) {
                window.subscribe(window.PUB_SUB_EVENTS.cartUpdate, async () => {
                    await refreshGrouping();
                });
            }
        }
        
        // v6.1: Multiple triggers for cart drawer timing issues
        document.addEventListener('cart:open', refreshGrouping);
        document.addEventListener('cart:opened', refreshGrouping);
        
        // v6.1: Cart icon click triggers (with retries)
        setupCartIconListener();
        
        // v6.1: MutationObserver fallback — watches drawer DOM changes
        setupCartDrawerObserver();
        
        // v6.1: Initial retries on page load (drawer may render late)
        setTimeout(refreshGrouping, 500);
        setTimeout(refreshGrouping, 1000);
        setTimeout(refreshGrouping, 2000);
        
        console.log('[OJ Gift Box Cart] Ready v6.1 — atomic bundle with multi-trigger grouping');
    }
    
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', init);
    } else {
        init();
    }
    
})();